(function () {
    "use strict";

    // =========================
    // Config
    // =========================
    const STOREFRONT_TOKEN = "f841e9d5e9c91d14fae42de580d33af5";

    // =========================
    // DOM & State Cache
    // =========================
    const els = {
        productSelector: () => document.getElementById("product-selector"),
        partCategories: () => document.getElementById("part-categories-container"),
        hotspotImg: () => document.getElementById("hotspot-image"),
        hotspotImgContainer: () => document.getElementById("hotspot-image-container"),
        accordionContainer: () => document.getElementById("hotspot-accordion"),
        spinner: () => document.getElementById("product-spinner"),
        zoomToggle: () => document.getElementById("zoom-toggle-btn"),
        zoomInBtn: () => document.getElementById("zoom-in-btn"),
        zoomOutBtn: () => document.getElementById("zoom-out-btn"),
    };

    // === NEW: Mobile-only pop label helpers ===
    const mq = window.matchMedia('(max-width: 767px)');
    let isMobile = mq.matches;
    mq.addEventListener?.('change', (e) => { isMobile = e.matches; });

    let currentPop = null;
    function closePop() {
        if (currentPop && currentPop.parentNode) {
            currentPop.parentNode.removeChild(currentPop);
            currentPop = null;
        }
    }

    // Close when tapping outside (mobile only)
    document.addEventListener('click', (e) => {
        if (!isMobile) return;
        const insidePop = currentPop && currentPop.contains(e.target);
        const onDot = e.target.closest && e.target.closest('.hotspot-dot');
        if (!insidePop && !onDot) closePop();
    }, true);

    // From Liquid/page
    const hotspotMap = window.hs_hotspotMap || null;
    const currentProductHandle = window.hs_productHandle || null;

    // =========================
    // Utilities
    // =========================
    const unique = (arr) => [...new Set(arr.filter(Boolean))];

    function formatMoney({ amount, currencyCode = "USD" }) {
        const num = Number.parseFloat(amount ?? "0");
        return new Intl.NumberFormat("en-US", { style: "currency", currency: currencyCode }).format(num);
    }


    // Build quick lookup for panels
    const panelById = new Map((hotspotMap?.panels ?? []).map(p => [String(p.id), p]));

    function getPanelForDot(dot) {
        return panelById.get(String(dot?.panelId)) || null;
    }

    // Normalize product ids to GIDs for Storefront API
    function toProductGID(id) {
        const s = String(id || "");
        return s.startsWith("gid://") ? s : `gid://shopify/Product/${s}`;
    }
    function normalizeProductGIDs(ids) {
        return [...new Set((ids || []).map(toProductGID))];
    }


    // =========================
    // Data (GraphQL) Calls
    // =========================
    async function gql(query, variables) {
        const res = await fetch("/api/2024-07/graphql.json", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-Shopify-Storefront-Access-Token": STOREFRONT_TOKEN,
            },
            body: JSON.stringify({ query, variables }),
        });
        const json = await res.json();
        return json?.data || {};
    }

    async function fetchProductsByTag(tag) {
        const query = `
      query getProductsByTag($first: Int, $query: String) {
        products(first: $first, query: $query) {
          edges { node { id title handle variants(first: 1) { edges { node { sku } } } } }
        }
      }`;
        const data = await gql(query, { first: 100, query: `tag:${tag}` });

        return (data.products?.edges ?? []).map(({ node }) => ({
            id: node.id,
            title: node.title,
            handle: node.handle,
            sku: node.variants?.edges?.[0]?.node?.sku || null,
        }));
    }

    async function fetchCollectionTitlesByIds(ids) {
        const uniques = unique(ids);
        if (!uniques.length) return {};

        const query = `
      query getCollections($ids: [ID!]!) {
        nodes(ids: $ids) { ... on Collection { id title } }
      }`;
        const data = await gql(query, { ids: uniques });

        const map = {};
        (data.nodes ?? []).forEach((n) => {
            if (n?.id && n?.title) map[n.id] = n.title;
        });
        return map;
    }

    async function fetchProductsByGIDs(productIdsOrGIDs) {
        const gids = normalizeProductGIDs(productIdsOrGIDs);
        if (!gids.length) return [];
        const query = `
    query ($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on Product {
          id title handle tags
          featuredImage { url altText }
          variants(first: 1) {
            edges { node { id price { amount currencyCode } } }
          }
        }
      }
    }`;
        const data = await gql(query, { ids: gids });
        return (data.nodes ?? []).filter(Boolean);
    }

    // =========================
    // UI Builders
    // =========================
    function populateProductSelector(products) {
        const select = els.productSelector();
        if (!select) return;

        products.forEach((p) => {
            if (p.handle === currentProductHandle) return;
            const opt = document.createElement("option");
            opt.value = p.handle;
            opt.textContent = p.title;
            select.appendChild(opt);
        });
    }

    function buildProductCard(p) {
        const card = document.createElement("div");
        card.className = "product-card";
        card.style.display = "inline-block";

        const variantId = p.variants?.edges?.[0]?.node?.id || "";
        const variantIdNumber = variantId.split("/").pop() || "";
        const priceObj = p.variants?.edges?.[0]?.node?.price;
        const priceAmount = Number.parseFloat(priceObj?.amount ?? "0");
        const hasServiceOnly = (p.tags ?? []).some((t) => t.toLowerCase() === "service only");

        const priceHtml =
            priceAmount > 0 && !hasServiceOnly
                ? `<div class="product-price">${formatMoney(priceObj)}</div>`
                : "";

        const actionBtnHtml = !hasServiceOnly
            ? `<button class="overview-atc-button red-button" onclick="addToCart('${variantIdNumber}')">ADD TO CART</button>`
            : `<button class="red-button" onclick="window.open('/products/${p.handle}', '_blank')">VIEW MORE</button>`;

        card.innerHTML = `
      <a href="/products/${p.handle}" target="_blank" class="product-anchor">
        <div class="product-image-wrapper">
          <div class="image-spinner"></div>
          <img src="${p.featuredImage?.url || ""}" alt="${p.featuredImage?.altText || p.title}" class="product-image"/>
        </div>
      </a>
      <div class="overview-info-wrapper">
        ${actionBtnHtml}
        <div class="product-title">${p.title}</div>
        ${priceHtml}
      </div>
    `;

        const img = card.querySelector(".product-image");
        const spin = card.querySelector(".image-spinner");
        if (img.complete) {
            img.classList.add("loaded");
            spin?.remove();
        } else {
            img.addEventListener("load", () => {
                img.classList.add("loaded");
                spin?.remove();
            });
        }

        return card;
    }

    function wirePanels(collectionTitleMap) {
        const accContainer = els.accordionContainer();
        if (!accContainer) return;

        accContainer.innerHTML = "";
        (hotspotMap.panels || []).forEach((panel) => {
            const item = buildAccordionItem(panel, collectionTitleMap);
            if (item) accContainer.appendChild(item);
        });
    }

    function buildAccordionItem(panel, collectionTitleMap) {
        const header = document.createElement("div");
        header.className = "accordion-header skeleton";
        header.setAttribute("data-panel-id", String(panel.id));
        if (panel?.collectionId) header.setAttribute("data-collection-id", panel.collectionId);

        // Prefer panel title; fall back to collection title (optional)
        const headerText =
            (panel?.title || "").trim() ||
            (panel?.collectionId ? (collectionTitleMap[panel.collectionId] || "") : "");

        header.textContent = headerText || "\u00A0"; // NBSP to preserve height

        const body = document.createElement("div");
        body.className = "accordion-body";
        body.id = `accordion-body-panel-${panel.id}`;

        const divider = document.createElement("div");
        divider.className = "accordion-divider";

        const item = document.createElement("div");
        item.className = "accordion-item";
        item.append(divider, header, body);

        header.addEventListener("click", () =>
            onAccordionHeaderClick({ item, body, panel })
        );

        return item;
    }

    // =========================
    // Behaviors
    // =========================
    async function onAccordionHeaderClick({ item, body, panel }) {
        const isOpen = body.classList.contains("active");
        if (isOpen) {
            collapseAllBodies();
            return;
        }

        collapseAllBodies();
        item.classList.add("open");

        if (body.dataset.loaded !== "true") {
            const gids = normalizeProductGIDs(panel?.productIds || []);
            if (!gids.length) {
                body.innerHTML = "<p>No products linked.</p>";
                body.dataset.loaded = "true";
            } else {
                const products = await fetchProductsByGIDs(gids);
                const grid = document.createElement("div");
                grid.className = "product-grid";
                products.forEach((p) => grid.appendChild(buildProductCard(p)));
                body.appendChild(grid);
                body.dataset.loaded = "true";
            }
        }

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                body.classList.add("active");
                body.style.maxHeight = body.scrollHeight + "px";
                body.querySelectorAll(".product-card").forEach((card, i) =>
                    setTimeout(() => card.classList.add("visible"), i * 100)
                );
            });
        });
    }

    function collapseAllBodies() {
        document.querySelectorAll(".accordion-body.active").forEach((b) => {
            b.classList.remove("active");
            b.style.maxHeight = null;
            b.querySelectorAll(".product-card.visible").forEach((c) => c.classList.remove("visible"));
        });
        document.querySelectorAll(".accordion-item.open").forEach((el) => el.classList.remove("open"));
    }

    function wireHotspotDots() {
        const container = els.hotspotImgContainer();
        if (!container || !(hotspotMap?.dots?.length)) return;

        hotspotMap.dots.forEach((dot) => {
            const panel = getPanelForDot(dot);

            // Dot element
            const el = document.createElement("div");
            el.className = "hotspot-dot";
            el.style.position = "absolute";
            el.style.left = dot.xPercent + "%";
            el.style.top = dot.yPercent + "%";
            el.dataset.panelId = panel ? String(panel.id) : "";

            // Tooltip (desktop only)
            if (dot.name && !isMobile) {
                const t = document.createElement("div");
                t.className = "hotspot-tooltip";
                t.textContent = dot.name;
                el.appendChild(t);
            }

            container.appendChild(el);

            el.addEventListener("click", (e) => {
                const pid = el.dataset.panelId;

                if (isMobile) {
                    e.preventDefault();
                    e.stopPropagation();

                    const dotId = String(dot.id);

                    // SECOND tap on same dot -> open accordion
                    if (currentPop && currentPop.dataset.forDot === dotId) {
                        closePop();
                        if (pid) expandAccordionByPanelId(pid);
                        return;
                    }

                    // FIRST tap -> show popup
                    closePop(); // close any other open popup

                    const title = (dot.tooltipText || dot.name || "View part").trim();

                    const pop = document.createElement("div");
                    pop.className = "hs-pop";
                    pop.dataset.forDot = dotId;
                    pop.innerHTML = `<span class="hs-label">${title}</span><button type="button" class="hs-go" aria-label="Go">➜</button>`;    
                    el.appendChild(pop);
                    currentPop = pop;

                    // SECOND tap on the POPUP itself -> open accordion
                    pop.addEventListener("click", (ev) => {
                        ev.preventDefault();
                        ev.stopPropagation();
                        closePop();
                        if (pid) expandAccordionByPanelId(pid);
                    });

                    return; // don't fall through to desktop behavior
                }

                // Desktop: expand immediately
                if (pid) expandAccordionByPanelId(pid);
            });
        });
    }

    async function expandAccordionByPanelId(panelId) {
        const categories = els.partCategories();
        const header = document.querySelector(`.accordion-header[data-panel-id='${panelId}']`);
        const body = document.getElementById(`accordion-body-panel-${panelId}`);
        if (!header || !body) return;

        if (!body.classList.contains("active")) {
            header.click(); // triggers onAccordionHeaderClick (lazy loads products)
        }
        categories?.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    function setupImageLoadingReveal() {
        const target = els.hotspotImg();
        const categories = els.partCategories();
        if (!target) return;

        const reveal = () => {
            target.classList.add("loaded");
            if (categories) categories.style.display = "block";
        };

        if (target.complete) reveal();
        else target.addEventListener("load", reveal);
    }

    function bindZoom() {
        const img = els.hotspotImgContainer();
        const zoomBtn = els.zoomToggle();
        const zoomInBtn = els.zoomInBtn();
        const zoomOutBtn = els.zoomOutBtn();
        if (!img || !zoomBtn) return;

        // State
        let zoom = 1;
        let translateX = 0;
        let translateY = 0;
        let zoomMode = false;
        let isDragging = false;
        let startX, startY;
        let initialPinchDistance = null;
        let initialZoom = zoom;
        let zoomHoldInterval = null;
        const minZoom = 1;
        const maxZoom = 3;
        const zoomStep = 0.15;
        const zoomHoldDelay = 50;

        const getPinchDistance = (t1, t2) => Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);

        function applyTransform() {
            const parent = img.parentElement;
            const parentW = parent.clientWidth;
            const parentH = parent.clientHeight;
            const scaledW = img.offsetWidth * zoom;
            const scaledH = img.offsetHeight * zoom;

            const maxX = Math.max(0, (scaledW - parentW) / 2);
            const maxY = Math.max(0, (scaledH - parentH) / 2);

            translateX = Math.max(-maxX, Math.min(maxX, translateX));
            translateY = Math.max(-maxY, Math.min(maxY, translateY));

            img.style.transform = `translate(${translateX}px, ${translateY}px) scale(${zoom})`;
        }

        function startZooming(direction) {
            if (zoomHoldInterval) return;
            const step = () => {
                if (direction === "in") {
                    zoomInBtn?.classList.add("active");
                    zoom = Math.min(zoom + zoomStep, maxZoom);
                } else {
                    zoomOutBtn?.classList.add("active");
                    zoom = Math.max(zoom - zoomStep, minZoom);
                }
                applyTransform();
            };
            step();
            zoomHoldInterval = setInterval(step, zoomHoldDelay);
        }

        function stopZooming() {
            clearInterval(zoomHoldInterval);
            zoomHoldInterval = null;
            zoomInBtn?.classList.remove("active");
            zoomOutBtn?.classList.remove("active");
        }

        // Mouse drag
        function onMouseDown(e) {
            if (!zoomMode || zoom <= 1) return;
            e.preventDefault();
            isDragging = true;
            startX = e.clientX - translateX;
            startY = e.clientY - translateY;
            img.style.cursor = "grabbing";
            img.style.transition = "none";
        }
        function onMouseMove(e) {
            if (!isDragging) return;
            translateX = e.clientX - startX;
            translateY = e.clientY - startY;
            applyTransform();
        }
        function onMouseUpOrLeave() {
            if (!isDragging) return;
            isDragging = false;
            img.style.cursor = "grab";
            img.style.transition = "transform 0.2s ease-out";
        }

        // Touch
        img.addEventListener(
            "touchstart",
            (e) => {
                if (!zoomMode) return;
                if (e.touches.length === 2) {
                    e.preventDefault();
                    initialPinchDistance = getPinchDistance(e.touches[0], e.touches[1]);
                    initialZoom = zoom;
                } else if (e.touches.length === 1 && zoom > 1) {
                    isDragging = true;
                    startX = e.touches[0].clientX - translateX;
                    startY = e.touches[0].clientY - translateY;
                    img.style.transition = "none";
                }
            },
            { passive: false }
        );

        img.addEventListener(
            "touchmove",
            (e) => {
                if (!zoomMode) return;
                if (e.touches.length === 2 && initialPinchDistance !== null) {
                    e.preventDefault();
                    const d = getPinchDistance(e.touches[0], e.touches[1]);
                    const scale = d / initialPinchDistance;
                    zoom = Math.min(Math.max(initialZoom * scale, minZoom), maxZoom);
                    applyTransform();
                } else if (e.touches.length === 1 && isDragging) {
                    e.preventDefault();
                    translateX = e.touches[0].clientX - startX;
                    translateY = e.touches[0].clientY - startY;
                    applyTransform();
                }
            },
            { passive: false }
        );

        img.addEventListener("touchend", (e) => {
            if (e.touches.length < 2) initialPinchDistance = null;
            if (e.touches.length === 0) {
                isDragging = false;
                img.style.transition = "transform 0.2s ease-out";
            }
        });

        // Wheel zoom
        img.addEventListener(
            "wheel",
            (e) => {
                if (!zoomMode) return;
                e.preventDefault();
                zoom += e.deltaY < 0 ? zoomStep : -zoomStep;
                zoom = Math.min(Math.max(zoom, minZoom), maxZoom);
                applyTransform();
            },
            { passive: false }
        );

        // Buttons & toggle
        els.zoomToggle()?.addEventListener("click", (e) => {
            e.stopPropagation();
            zoomMode = !zoomMode;
            zoomBtn.classList.toggle("active", zoomMode);
            if (els.zoomInBtn()) els.zoomInBtn().style.display = zoomMode ? "block" : "none";
            if (els.zoomOutBtn()) els.zoomOutBtn().style.display = zoomMode ? "block" : "none";

            if (zoomMode) {
                img.style.transition = "transform 0.2s ease-out";
                zoom = 1.5;
                img.style.cursor = "grab";
                applyTransform();
            } else {
                zoom = 1;
                translateX = 0;
                translateY = 0;
                img.style.transition = "transform 0.2s ease-out";
                img.style.cursor = "default";
                applyTransform();
            }
        });

        els.zoomInBtn()?.addEventListener("mousedown", (e) => {
            e.stopPropagation();
            startZooming("in");
        });
        els.zoomOutBtn()?.addEventListener("mousedown", (e) => {
            e.stopPropagation();
            startZooming("out");
        });
        ["mouseup", "mouseleave", "touchend"].forEach((ev) => {
            els.zoomInBtn()?.addEventListener(ev, stopZooming);
            els.zoomOutBtn()?.addEventListener(ev, stopZooming);
        });
        els.zoomInBtn()?.addEventListener(
            "touchstart",
            (e) => {
                e.preventDefault();
                startZooming("in");
            },
            { passive: false }
        );
        els.zoomOutBtn()?.addEventListener(
            "touchstart",
            (e) => {
                e.preventDefault();
                startZooming("out");
            },
            { passive: false }
        );

        // Mouse drag bindings
        img.addEventListener("mousedown", onMouseDown);
        img.addEventListener("mousemove", onMouseMove);
        img.addEventListener("mouseup", onMouseUpOrLeave);
        img.addEventListener("mouseleave", onMouseUpOrLeave);
    }

    // =========================
    // Bootstrapping
    // =========================
    document.addEventListener("DOMContentLoaded", async () => {
        setupImageLoadingReveal();

        // Populate selector & ss
        try {
            const products = await fetchProductsByTag("HotSpots");
            els.spinner()?.classList.add("hidden");
            populateProductSelector(products);
            window.initializeSlimSelect(products); // global initializer stays the same
        } catch (e) {
            console.error("Failed to load HotSpots products", e);
            els.spinner()?.classList.add("hidden");
        }

        if (!hotspotMap?.imageUrl) return;

        const collectionIds = (hotspotMap.panels ?? [])
            .map((p) => p.collectionId)
            .filter(Boolean);

        const collectionTitleMap = await fetchCollectionTitlesByIds(collectionIds);

        wirePanels(collectionTitleMap);
        wireHotspotDots();

        // Fill any skeleton headers after titles resolve
        unique(collectionIds).forEach((cid) => {
            document
                .querySelectorAll(`.accordion-header[data-collection-id='${cid}']`)
                .forEach((el) => {
                    el.classList.remove("skeleton");
                    if (!el.textContent?.trim()) el.textContent = collectionTitleMap[cid] || "";
                });
        });

        // Zoom/pan
        bindZoom();
    });

    // =========================
    // Public (globals to match existing usage)
    // =========================
    window.addToCart = function addToCart(variantId) {
        fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                id: variantId,
                quantity: 1,
                sections: "cart-drawer,cart-icon-bubble",
            }),
        })
            .then(async (res) => {
                if (!res.ok) throw new Error("Add to cart failed");
                const json = await res.json();
                const drawerMarkup = json.sections["cart-drawer"];
                const bubbleMarkup = json.sections["cart-icon-bubble"];

                if (document.getElementById("cart-icon-bubble")) {
                    document.getElementById("cart-icon-bubble").innerHTML = bubbleMarkup;
                }

                const parser = new DOMParser();
                const newDrawerDoc = parser.parseFromString(drawerMarkup, "text/html");
                let drawer = document.querySelector(".drawer");
                drawer.outerHTML = newDrawerDoc.querySelector(".drawer").outerHTML;

                drawer = document.querySelector(".drawer");
                drawer.classList.add("animate");
                drawer.querySelector(".drawer__inner").style = "padding-right:30px";
                setTimeout(() => drawer.classList.add("active"), 0);
            })
            .catch((err) => console.error("Error adding to cart:", err));
    };

    window.initializeSlimSelect = function initializeSlimSelect(products) {
        return new window.SlimSelect({
            select: "#product-selector",
            events: {
                searchFilter: (option, search) => {
                    const handle = option.value;
                    const product = products.find((p) => p.handle === handle);
                    if (!product) return false;

                    const q = (search || "").toLowerCase();
                    const title = option.text.toLowerCase();
                    const sku = (product.sku || "").toLowerCase();
                    const last3 = sku.slice(-3);

                    return title.includes(q) || (last3 && q.includes(last3)) || sku.includes(q);
                },
                afterChange: (info) => {
                    const selectedHandle = info?.[0]?.value;
                    if (selectedHandle) {
                        const arrow = document.querySelector(".ss-arrow");
                        if (arrow) arrow.style.display = "none";
                        const sp = els.spinner();
                        if (sp) sp.classList.remove("hidden");
                        window.location.href = `/products/${selectedHandle}?view=hotspots`;
                    }
                },
            },
        });
    };
})();
